// src/components/common/TrackingScripts.js — Maxula
// Injection conditionnelle des pixels marketing : Meta Pixel, Google Analytics (GA4),
// TikTok Pixel. Les identifiants viennent des Paramètres admin (PlatformSettings).
// RGPD : aucun script n'est chargé tant que le consentement n'est pas accordé.
// Événements de conversion exposés via window.mxTrack(event, data).
// v1.381 — AbodyStudio Limited

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsAPI } from '../../services/api';

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  const low = s.toLowerCase();
  if (!s || low === 'null' || low === 'undefined' || low === 'false') return '';
  return s;
};

// Le consentement est lu depuis localStorage (posé par le bandeau CookieConsent).
const hasConsent = () => {
  try { return localStorage.getItem('mx_cookie_consent') === 'accepted'; } catch (e) { return false; }
};

// ---- Injecteurs (chacun ne s'exécute qu'une seule fois) ----
const injectMetaPixel = (id) => {
  if (!id || window._mxMetaLoaded) return;
  window._mxMetaLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  try { window.fbq('init', id); window.fbq('track', 'PageView'); } catch (e) {}
};

const injectGA = (id) => {
  if (!id || window._mxGaLoaded) return;
  window._mxGaLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: true });
};

const injectTikTok = (id) => {
  if (!id || window._mxTtLoaded) return;
  window._mxTtLoaded = true;
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || [];
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e; };
    ttq.load = function (e, n) {
      var r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r; ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
      ttq._o = ttq._o || {}; ttq._o[e] = n || {};
      var o = d.createElement('script'); o.type = 'text/javascript'; o.async = !0; o.src = r + '?sdkid=' + e + '&lib=' + t;
      var a = d.getElementsByTagName('script')[0]; a.parentNode.insertBefore(o, a);
    };
    ttq.load(id); ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable */
};

// Helper global de conversion : appelé partout via window.mxTrack('Purchase', {...}).
// Il route l'événement vers les 3 pixels avec la bonne nomenclature.
const installGlobalTracker = () => {
  if (window.mxTrack) return;
  window.mxTrack = (event, data = {}) => {
    const value = Number(data.value || data.amount || 0) || 0;
    const currency = data.currency || 'MAD';
    try {
      // Meta Pixel (événements standard)
      if (window.fbq) {
        const metaMap = { Purchase: 'Purchase', AddToCart: 'AddToCart', Registration: 'CompleteRegistration' };
        const m = metaMap[event] || event;
        window.fbq('track', m, value ? { value, currency, ...data } : data);
      }
      // Google Analytics 4 (events)
      if (window.gtag) {
        const gaMap = { Purchase: 'purchase', AddToCart: 'add_to_cart', Registration: 'sign_up' };
        const g = gaMap[event] || event;
        window.gtag('event', g, value ? { value, currency, ...data } : data);
      }
      // TikTok Pixel
      if (window.ttq) {
        const ttMap = { Purchase: 'CompletePayment', AddToCart: 'AddToCart', Registration: 'CompleteRegistration' };
        const tt = ttMap[event] || event;
        window.ttq.track(tt, value ? { value, currency, ...data } : data);
      }
    } catch (e) {}
  };
};

const TrackingScripts = () => {
  const location = useLocation();
  const ids = useRef({ meta: '', ga: '', tiktok: '', enabled: true, ready: false });

  // 1) Charger les identifiants depuis les paramètres publics, puis injecter si consentement.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await settingsAPI.get();
        const s = res.data?.data || {};
        if (!mounted) return;
        ids.current = {
          meta: clean(s.metaPixelId),
          ga: clean(s.googleAnalyticsId),
          tiktok: clean(s.tiktokPixelId),
          enabled: s.trackingEnabled !== false,
          ready: true
        };
        maybeInject();
      } catch (e) { /* silencieux : pas de tracking si settings indisponibles */ }
    })();

    // Réagir à l'acceptation des cookies (événement custom émis par CookieConsent).
    const onConsent = () => maybeInject();
    window.addEventListener('mx-consent-changed', onConsent);
    return () => { mounted = false; window.removeEventListener('mx-consent-changed', onConsent); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maybeInject = () => {
    const cfg = ids.current;
    if (!cfg.ready || !cfg.enabled) return;
    if (!hasConsent()) return; // RGPD : rien sans consentement
    installGlobalTracker();
    if (cfg.meta) injectMetaPixel(cfg.meta);
    if (cfg.ga) injectGA(cfg.ga);
    if (cfg.tiktok) injectTikTok(cfg.tiktok);
  };

  // 2) Suivre les changements de page (SPA) → PageView sur chaque pixel actif.
  useEffect(() => {
    if (!hasConsent()) return;
    try {
      if (window.fbq) window.fbq('track', 'PageView');
      if (window.gtag && ids.current.ga) window.gtag('config', ids.current.ga, { page_path: location.pathname + location.search });
      if (window.ttq) window.ttq.page();
    } catch (e) {}
  }, [location.pathname, location.search]);

  return null;
};

export default TrackingScripts;
